import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Wallet, Zap, Clock, CheckCircle, Lock, Settings, Activity, Key, AlertCircle, Menu, X, HelpCircle, ArrowRight } from 'lucide-react';
import ChainSelector from './ChainSelector';
import EVMWalletButton from './EVMWalletButton';
import '../index.css';

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const sectionsRef = useRef([]);

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Intersection Observer for scroll animations
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      sectionsRef.current.forEach((section) => {
        if (section) observer.observe(section);
      });

      return () => observer.disconnect();
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solutions = [
    {
      title: 'MIGRATION',
      description: 'Click here for migration or to resolve any migration related issues.',
    },
    {
      title: 'RECTIFICATION',
      description: 'Click here to rectify all strange wallet issues.',
    },
    {
      title: 'CLAIM',
      description: 'Click here to resolve tokens or resolve any token resolving related issues.',
    },
    {
      title: 'SWAP',
      description: 'Click here to swap tokens or resolve issues related to token swap.',
    },
    {
      title: 'SLIPPAGE',
      description: 'Click here for slippage or transaction fee related issues.',
    },
    {
      title: 'CLAIM AIRDROP',
      description: 'Click here to resolve airdrop or resolve errors encountered during airdrop resolve.',
    },
    {
      title: 'STAKING',
      description: 'Click here to resolve issues encountered while staking/unstaking.',
    },
    {
      title: 'WHITELIST',
      description: 'Click here to whitelist your address or resolve whitelisting related error.',
    },
    {
      title: 'CROSS TRANSFER',
      description: 'Click here to resolve cross bridging errors encountered during cross transfer.',
    },
    {
      title: 'NFTs',
      description: 'Click here to resolve NFT related issues.',
    },
    {
      title: 'LOCKED ACCOUNT',
      description: 'Click here to resolve locked account or wallet stuck issues.',
    },
    {
      title: 'LOGIN ERROR',
      description: 'Click here to resolve errors encountered during login.',
    },
    {
      title: 'WALLET GLITCH',
      description: 'Click here to resolve wallet issues.',
    },
    {
      title: 'DEFI FARMING',
      description: 'Click here for DeFi or Commercial farming related issues.',
    },
    {
      title: 'VALIDATION',
      description: 'Click here to validate your wallet via Multisig.',
    },
    {
      title: 'TRANSACTION DELAY',
      description: 'Click here for any transaction related error.',
    },
    {
      title: 'MISSING/IRREGULAR BALANCE',
      description: 'Click here to recover lost or missing funds.',
    },
    {
      title: 'RECOVERY',
      description: 'Click here for wallet recovery.',
    },
    {
      title: 'BUY TOKEN/COIN',
      description: 'Click here to trade. Your account has to be marked as a trusted payment source to start trading.',
    },
    {
      title: 'EXCHANGE',
      description: 'Click here for token exchange or to resolve errors encountered during token exchange.',
    },
    {
      title: 'BRIDGING',
      description: 'Click here to bridge tokens or resolve bridging related issues.',
    },
    {
      title: 'REVOKE',
      description: 'Click here to revoke token approvals or resolve approval related issues.',
    },
    {
      title: 'SYNCHRONIZATION',
      description: 'Click here to synchronize your wallet or resolve synchronization related issues.',
    },
  ];

  return (
    <div className="min-h-screen text-white overflow-x-hidden relative">
      {/* Animated Background Particles */}
      <div className="particles">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="particle" />
        ))}
      </div>
      
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-[9998] transition-all duration-500 ${
        scrolled 
          ? 'glass-dark shadow-glow-lg border-b border-white/20' 
          : 'glass-dark border-b border-white/10'
      } ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-glow transform hover:scale-110 transition-transform duration-300">
                <span className="text-white font-bold text-lg sm:text-xl">E</span>
              </div>
              <span className="font-bold text-xl sm:text-2xl text-gradient">EVSectionResolver</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {['Resolve Issues', 'Synchronize', 'Solutions', 'Work'].map((item, index) => (
                <Link 
                  key={item}
                  to="/connect" 
                  className="text-white/80 hover:text-white transition-all duration-300 font-medium relative group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-primary transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ))}
            </nav>
            
            <div className="flex items-center space-x-1 sm:space-x-4">
              <div className="hidden sm:block">
                <ChainSelector />
              </div>
              <div className="hidden sm:block">
                <EVMWalletButton />
              </div>
              
              {/* Mobile: Chain Selector and Wallet Button */}
              <div className="sm:hidden flex items-center space-x-1">
                <ChainSelector />
                <EVMWalletButton />
              </div>
              
              {/* Mobile menu button */}
              <button
                className="md:hidden p-1.5 sm:p-2 rounded-lg text-white/80 hover:text-white glass hover:shadow-glow transition-all duration-300"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden glass-dark border-t border-white/10 transition-all duration-300 ${mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="px-4 py-4 space-y-4">
            {['Resolve Issues', 'Synchronize', 'Solutions', 'Work'].map((item, index) => (
              <Link 
                key={item}
                to="/connect" 
                className="block text-white/80 hover:text-white transition-all duration-300 py-2 font-medium transform hover:translate-x-2"
                onClick={() => setMobileMenuOpen(false)}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {item}
              </Link>
            ))}
            <div className="flex justify-center">
              <EVMWalletButton />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section ref={(el) => (sectionsRef.current[0] = el)} className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="space-y-6">
                {/* Dapps Help Desk Label */}
                <div className="flex items-center space-x-2 animate-fade-in-up">
                  <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center shadow-glow">
                    <HelpCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white/90 font-medium text-sm">EVM Token Resolution</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-slide-in-left">
                  <span className="text-white">Resolve Your </span>
                  <span className="text-gradient animate-pulse-slow">EVM Token Issues</span>
                </h1>
                <p className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl animate-fade-in-up" style={{ animationDelay: '200ms' }}>
                  Locked out of your wallet? Missing funds? Transaction errors? Our expert team provides fast, secure solutions to get your crypto back on track.
                </p>
              </div>
              <Link 
                to="/connect"
                className="inline-flex items-center space-x-2 bg-gradient-primary hover:shadow-glow-lg text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-glow transform hover:scale-105 hover:-translate-y-1 animate-bounce-in group"
                style={{ animationDelay: '400ms' }}
              >
                <span>Resolve Issues</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            
            <div className="glass rounded-2xl p-6 sm:p-8 shadow-glow-lg border border-white/20 animate-slide-in-right hover:shadow-glow transition-all duration-500 glow-hover">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Portfolio Dashboard</h3>
                  <p className="text-gray-400">Real-time crypto monitoring & recovery</p>
                </div>
                
                <div className="space-y-4">
                  {[
                    { name: 'Bitcoin', value: '$120,465.00', change: '+1.00%', color: 'orange', trend: 'up' },
                    { name: 'Ethereum', value: '$4,695.80', change: '+0.79%', color: 'green', trend: 'up' },
                    { name: 'Bitcoin Cash', value: '$610.34', change: '-0.38%', color: 'orange', trend: 'down' },
                    { name: 'Litecoin', value: '$132.82', change: '+1.41%', color: 'green', trend: 'up' }
                  ].map((crypto, index) => (
                    <div 
                      key={crypto.name}
                      className="flex justify-between items-center p-4 bg-gray-800/50 rounded-xl border border-gray-700/30 hover:bg-gray-700/50 transition-all duration-300 transform hover:scale-105 animate-fade-in-up"
                      style={{ animationDelay: `${600 + index * 100}ms` }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 bg-${crypto.color}-400 rounded-full animate-pulse`}></div>
                        <span className="font-medium">{crypto.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{crypto.value}</div>
                        <div className={`text-${crypto.trend === 'up' ? 'green' : 'red'}-400 text-sm font-medium animate-pulse`}>
                          {crypto.change}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-gray-500 text-sm font-medium animate-fade-in">Live Market Data</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work with us Section */}
      <section ref={(el) => (sectionsRef.current[1] = el)} className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-800/30 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            {/* Work with us Label */}
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-gray-400 font-medium text-sm">Work with us</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Let's solve crypto wallet issues—
              <span className="text-gradient animate-pulse-slow">together.</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Whether you're an individual, crypto platform, or service provider, our team offers custom wallet support, seamless integration, and trusted recovery services built for scale and security.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              {[
                { icon: Shield, title: "Expert Wallet Recovery", desc: "Professional recovery services for lost or corrupted wallets with guaranteed security." },
                { icon: Settings, title: "Blockchain Solutions", desc: "Comprehensive blockchain troubleshooting and migration services." },
                { icon: Lock, title: "Secure Troubleshooting Services", desc: "Military-grade security protocols for all wallet recovery operations." }
              ].map((service, index) => (
                <div 
                  key={service.title}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="w-14 h-14 bg-gradient-primary rounded-xl flex items-center justify-center mb-6 shadow-glow transform hover:rotate-12 transition-transform duration-300">
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="glass rounded-2xl p-8 border border-white/20 shadow-glow-lg animate-slide-in-right glow-hover transition-all duration-500">
              <div className="w-20 h-20 bg-gradient-primary rounded-2xl flex items-center justify-center mb-8 shadow-glow transform hover:scale-110 transition-transform duration-300">
                <Wallet className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Professional Team</h3>
              <p className="text-gray-400 text-lg leading-relaxed">Expert developers and security specialists ready to help with years of experience in blockchain technology and crypto recovery.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting Process */}
      <section ref={(el) => (sectionsRef.current[2] = el)} className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            {/* Troubleshooting Process Label */}
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-gray-400 font-medium text-sm">Troubleshooting Process</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
              Whether you're locked out or just confused, our team can step in at any stage and get your wallet back on track.
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: 1, title: "Issue Detection", desc: "We assess key systems and collect wallet or transaction details to identify the root cause." },
              { number: 2, title: "Verification", desc: "We verify ownership, wallet type, and confirm the root cause of the issue." },
              { number: 3, title: "Recovery Plan", desc: "We apply a tailored fix—ranging from seed phrase restoration to syncing issues." },
              { number: 4, title: "Aftercare", desc: "We ensure full access and guide you on securing your wallet for the future." }
            ].map((step, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 text-center border border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-glow transform hover:rotate-12 transition-transform duration-300">
                  <span className="text-white font-bold text-xl">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Secure Wallet Recovery */}
      <section ref={(el) => (sectionsRef.current[3] = el)} className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-800/30 to-transparent">
        <div className="max-w-7xl mx-auto text-center animate-fade-in-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-gradient animate-pulse-slow">Built for Secure Wallet Recovery</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-4xl mx-auto leading-relaxed">
            Whether it's a lost seed phrase, broken sync, or locked access, our platform helps you regain control with expert support and advanced recovery tools.
          </p>
          <Link 
            to="/connect"
            className="inline-flex items-center space-x-2 bg-gradient-primary hover:shadow-glow-lg text-white px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-glow transform hover:scale-105 hover:-translate-y-1 animate-bounce-in group"
          >
            <span>Resolve Web3 Issues</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      {/* Wallet Troubleshooting */}
      <section ref={(el) => (sectionsRef.current[4] = el)} className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-in-left">
              <div className="space-y-6">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                  Diagnose, fix, and recover your crypto wallet with confidence.
                </h2>
                <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                  From lost access to corrupted files or stuck transactions, we've built a simple solution to get you back on track—fast and securely.
                </p>
              </div>
              
              <div className="space-y-6">
                {[
                  { icon: Activity, title: "Automated Wallet Diagnosis", desc: "AI-powered diagnostics that identify issues instantly and provide actionable solutions." },
                  { icon: Key, title: "Recovery Options for Lost Keys", desc: "Multiple recovery methods including seed phrase restoration and key regeneration." },
                  { icon: Lock, title: "Military-grade Security & Privacy", desc: "Bank-level encryption and zero-knowledge protocols protect your sensitive data." }
                ].map((feature, index) => (
                  <div 
                    key={feature.title}
                    className="flex items-start space-x-4 animate-fade-in-up"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow flex-shrink-0 transform hover:rotate-12 transition-transform duration-300">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                      <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="glass rounded-2xl p-6 sm:p-8 border border-white/20 shadow-glow-lg animate-slide-in-right glow-hover transition-all duration-500">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Wallet Recovery Dashboard</h3>
                  <p className="text-gray-400">Advanced diagnostics & security tools</p>
                </div>
                
                <div className="space-y-4">
                  {[
                    { label: "Wallet Status", status: "Healthy", icon: CheckCircle, color: "green" },
                    { label: "Transaction Issues", status: "2 Found", icon: AlertCircle, color: "orange" },
                    { label: "Security Level", status: "High", icon: Shield, color: "blue" }
                  ].map((item, index) => (
                    <div 
                      key={item.label}
                      className="flex justify-between items-center p-4 bg-gray-800/50 rounded-xl border border-gray-700/30 hover:bg-gray-700/50 transition-all duration-300 transform hover:scale-105 animate-fade-in-up"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <span className="font-medium">{item.label}</span>
                      <div className="flex items-center space-x-2">
                        <item.icon className={`w-5 h-5 text-${item.color}-400 animate-pulse`} />
                        <span className={`text-${item.color}-400 font-semibold`}>{item.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upgrade Section */}
      <section ref={(el) => (sectionsRef.current[5] = el)} className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-800/30 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-in-left">
              <div className="space-y-6">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                  <span className="text-gradient animate-pulse-slow">Enhance Your Wallet Security & Performance</span>
                </h2>
                <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                  Improve your wallet's reliability, restore lost functionality, and future-proof your crypto access with our advanced troubleshooting suite.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Wallet Load Speed",
                  "Multi-Chain Support", 
                  "Enhanced Transaction Recovery",
                  "Real-time Error Detection"
                ].map((feature, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-3 animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 animate-pulse-slow" />
                    <span className="font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="glass rounded-2xl p-6 sm:p-8 border border-white/20 shadow-glow-lg animate-slide-in-right glow-hover transition-all duration-500">
              <h3 className="text-2xl font-bold mb-8">Performance Metrics</h3>
              
              <div className="space-y-6">
                {[
                  { label: "Load Speed", value: 95 },
                  { label: "Security", value: 100 },
                  { label: "Recovery Rate", value: 98 },
                  { label: "Uptime", value: 99.9 }
                ].map((metric, index) => (
                  <div 
                    key={index}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="flex justify-between mb-3">
                      <span className="font-medium">{metric.label}</span>
                      <span className="font-bold text-primary-400">{metric.value}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-gradient-primary h-3 rounded-full transition-all duration-1000 ease-out animate-progress"
                        style={{width: `${metric.value}%`}}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Always By Your Side */}
      <section ref={(el) => (sectionsRef.current[6] = el)} className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center animate-fade-in-up">
          {/* Always By Your Side Label */}
          <div className="flex items-center justify-center space-x-2 mb-4">
            <span className="text-primary-400 font-medium text-sm">Always By Your Side</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Powerful Wallet Recovery. Zero Panic.
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {[
              {
                icon: Shield,
                title: "Safety First",
                desc: "Our system prioritizes security and privacy with no compromise."
              },
              {
                icon: Zap,
                title: "Lightning Fast Fixes",
                desc: "Diagnose and resolve most wallet issues in under 5 minutes."
              },
              {
                icon: Clock,
                title: "24/7 Support",
                desc: "Help is always available—anytime, anywhere."
              }
            ].map((feature, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg transform hover:rotate-12 transition-transform duration-300">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wallet Solutions Grid Section */}
      <section ref={(el) => (sectionsRef.current[7] = el)} className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-800/30 to-transparent">
        <div className="max-w-7xl mx-auto text-center animate-fade-in-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-gradient animate-pulse-slow">Wallet Solutions</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            Select your specific issue from our comprehensive list of wallet troubleshooting solutions. Our expert team is ready to help resolve any crypto wallet problem you're experiencing.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((solution, index) => (
              <Link
                key={index}
                to="/connect"
                className="glass rounded-2xl p-6 shadow-glow border border-white/20 flex flex-col items-center text-center transform hover:scale-105 hover:shadow-glow-lg transition-all duration-300 animate-fade-in-up glow-hover"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <h3 className="text-lg font-bold text-white mb-3">{solution.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{solution.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer ref={(el) => (sectionsRef.current[8] = el)} className="bg-gradient-to-b from-gray-800 to-gray-900 border-t border-gray-700/50 py-12 animate-fade-in-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-6 md:mb-0">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center shadow-glow transform hover:scale-110 transition-transform duration-300">
                <span className="text-white font-bold text-sm">E</span>
              </div>
              <span className="font-bold text-xl text-gradient">EVSectionResolver</span>
            </div>
            
            <div className="flex flex-wrap justify-center md:justify-end gap-6 text-sm text-gray-400">
              {['Terms', 'Privacy', 'Support', 'Latest News'].map((link, index) => (
                <Link 
                  key={link}
                  to="/connect" 
                  className="hover:text-primary-400 transition-all duration-300 font-medium transform hover:scale-105"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {link}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes bounce-in {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: var(--progress-width);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-bounce-in {
          animation: bounce-in 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-progress {
          animation: progress 1.5s ease-out forwards;
        }

        .animate-in {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default HomePage; 